import type { ReactNode } from "react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import type { BotsProblem } from "./settings/botsValidation";
import type { BotSettingsDraft } from "./useBotSettingsDraft";
import styles from "./ProjectConfigDialogs.module.css";

/**
 * The two dialogs every config form on the Bots page needs, and the alert that
 * names what a refusal refused.
 *
 * They are here rather than in each tab because they are the same two
 * questions in the same two sentences: throw the edits away, or take somebody
 * else's newer version. Both were written out verbatim in Settings and in
 * Source, down to the punctuation.
 */
export function ProjectConfigDialogs({
  draft,
  changes,
  sections,
}: {
  draft: BotSettingsDraft;
  /** What the Discard dialog counts — the caller's own walk over the keys its
   *  form owns, which is not the same list on the two tabs. */
  changes: number;
  sections: number;
}) {
  return (
    <>
      <Modal
        open={draft.discardOpen}
        onClose={draft.closeDiscard}
        title="Discard unsaved changes?"
        footer={
          <>
            <Button variant="secondary" onClick={draft.closeDiscard}>
              Cancel
            </Button>
            <Button variant="danger" onClick={draft.discard}>
              Discard
            </Button>
          </>
        }
      >
        <p>
          You will lose {changes} {changes === 1 ? "change" : "changes"} across{" "}
          {sections} {sections === 1 ? "section" : "sections"}. This can't be
          undone.
        </p>
      </Modal>

      <Modal
        open={draft.conflictOpen}
        onClose={draft.closeConflict}
        title="Config changed by someone else"
        footer={
          <>
            <Button variant="secondary" onClick={draft.closeConflict}>
              Cancel
            </Button>
            <Button onClick={() => void draft.reload()}>Reload page</Button>
          </>
        }
      >
        <p>
          Another user saved a newer version of this config. Reload to load
          their changes — your current edits will be discarded.
        </p>
      </Modal>
    </>
  );
}

/**
 * What a refusal named, or what the browser already knows cannot be saved.
 *
 * One component for both cases and both tabs: the difference between them is
 * the title, and a screen that listed a server's complaint one way and its own
 * the other would be two vocabularies for one list of fields.
 */
export function ConfigProblems({
  title,
  intro,
  problems,
  after,
}: {
  title: string;
  /** The sentence above the list, when the refusal came with one. */
  intro?: string;
  problems: BotsProblem[];
  /**
   * One line UNDER the list — the way out, for a screen that cannot fix what
   * the list names. The Config page needs it: editing a card schema there can
   * turn the bot settings into a document that stops the simulation, so the
   * save is refused with THEIR field names, and none of those fields is on that
   * page.
   */
  after?: ReactNode;
}) {
  return (
    <Alert variant="danger" title={title}>
      {intro}
      {problems.length > 0 && (
        <ul className={styles.problemList}>
          {problems.map((p) => (
            <li key={p.path + p.message}>
              <code>{p.path}</code> — {p.message}
            </li>
          ))}
        </ul>
      )}
      {after}
    </Alert>
  );
}
